import React, { useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import {
    getStoredCookieConsent,
    initializeAnalytics,
    isDownloadUrl,
    setDefaultConsentMode,
    trackEvent,
    trackPageView,
    trackTiming,
    updateConsentMode,
} from "../../utils/analytics";
import { applySeo, getSeoConfig } from "../../utils/seo";

const getElementLabel = (element: Element) => {
    const ariaLabel = element.getAttribute("aria-label");
    const title = element.getAttribute("title");
    const text = element.textContent?.replace(/\s+/g, " ").trim();

    return ariaLabel || title || text?.slice(0, 120) || element.tagName.toLowerCase();
};

const getPagePath = () => `${window.location.pathname}${window.location.search}${window.location.hash}`;

const AnalyticsManager: React.FC = () => {
    const location = useLocation();
    const pageStartedAt = useRef(Date.now());
    const activePath = useRef(getPagePath());
    const scrollDepthByPath = useRef<Record<string, Set<number>>>({});

    useEffect(() => {
        setDefaultConsentMode();
        if (getStoredCookieConsent()?.analytics) {
            initializeAnalytics();
        }

        const handleConsentChange = (event: Event) => {
            const detail = (event as CustomEvent<{ analytics: boolean }>).detail;
            updateConsentMode(!!detail?.analytics);
            if (detail?.analytics) {
                initializeAnalytics();
                trackEvent("cookie_consent_update", {
                    event_category: "privacy",
                    event_label: "analytics_granted",
                });
                trackPageView(getPagePath(), document.title);
            }
        };

        window.addEventListener("cyber-cookie-consent-change", handleConsentChange);
        return () => window.removeEventListener("cyber-cookie-consent-change", handleConsentChange);
    }, []);

    useEffect(() => {
        const previousPath = activePath.current;
        const now = Date.now();
        trackTiming("page_time", now - pageStartedAt.current, previousPath);

        const seo = getSeoConfig(location.pathname);
        applySeo(seo);

        const currentPath = `${location.pathname}${location.search}${location.hash}`;
        activePath.current = currentPath;
        pageStartedAt.current = now;
        trackPageView(currentPath, seo.title);
    }, [location]);

    useEffect(() => {
        const handleBeforeUnload = () => {
            trackTiming("page_time", Date.now() - pageStartedAt.current, activePath.current);
        };

        window.addEventListener("beforeunload", handleBeforeUnload);
        return () => window.removeEventListener("beforeunload", handleBeforeUnload);
    }, []);

    useEffect(() => {
        const thresholds = [25, 50, 75, 90];

        const handleScroll = () => {
            const pageHeight = document.documentElement.scrollHeight - window.innerHeight;
            if (pageHeight <= 0) return;

            const depth = Math.round((window.scrollY / pageHeight) * 100);
            const path = activePath.current;
            scrollDepthByPath.current[path] = scrollDepthByPath.current[path] || new Set<number>();

            thresholds.forEach((threshold) => {
                if (depth >= threshold && !scrollDepthByPath.current[path].has(threshold)) {
                    scrollDepthByPath.current[path].add(threshold);
                    trackEvent("scroll_depth", {
                        event_category: "engagement",
                        event_label: path,
                        value: threshold,
                    });
                }
            });
        };

        window.addEventListener("scroll", handleScroll, { passive: true });
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    useEffect(() => {
        const handleClick = (event: MouseEvent) => {
            const target = event.target instanceof Element ? event.target : null;
            const element = target?.closest("a,button");
            if (!element) return;

            const label = getElementLabel(element);
            const pagePath = activePath.current;

            if (element instanceof HTMLAnchorElement) {
                const href = element.href;
                const rawHref = element.getAttribute("href") || "";

                if (rawHref.startsWith("mailto:") || href.startsWith("mailto:")) {
                    trackEvent("contact_click", {
                        event_category: "contact",
                        event_label: rawHref,
                        link_text: label,
                    });
                    return;
                }

                if (rawHref.startsWith("tel:") || href.startsWith("tel:")) {
                    trackEvent("contact_click", {
                        event_category: "contact",
                        event_label: rawHref,
                        link_text: label,
                    });
                    return;
                }

                if (element.hasAttribute("download") || isDownloadUrl(href)) {
                    trackEvent("file_download", {
                        event_category: "download",
                        event_label: href,
                        link_text: label,
                    });
                    return;
                }

                if (href && element.hostname && element.hostname !== window.location.hostname) {
                    trackEvent("outbound_click", {
                        event_category: "outbound",
                        event_label: href,
                        link_text: label,
                    });
                    return;
                }

                trackEvent("navigation_click", {
                    event_category: "navigation",
                    event_label: rawHref || href,
                    link_text: label,
                    page_path: pagePath,
                });
                return;
            }

            if (element instanceof HTMLButtonElement) {
                trackEvent("button_click", {
                    event_category: "interaction",
                    event_label: label,
                    page_path: pagePath,
                });
            }
        };

        document.addEventListener("click", handleClick, true);
        return () => document.removeEventListener("click", handleClick, true);
    }, []);

    return null;
};

export default AnalyticsManager;
